import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, ShieldCheck, ShieldAlert, FileText } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { GstinInput, fillFrom } from "@/components/gstinFetch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, errMsg, inr, fmtDate } from "@/lib/api";
import { PageHeader, SearchBox, Field, TableWrap, Empty, th, td, tr } from "@/components/common";
import { DocHistory } from "@/components/docs";
import { OpeningFields, openingBody } from "@/components/opening";
import { VENDOR_CATS, KYC_TYPES, DocUpload, useMarketRoles, nativeInput } from "@/components/market";
import { useDriverRules, DriverRulesDialog } from "@/components/driverRules";

const EP = "/market/vendor-field-rules";
const star = (on) => (on ? <span className="text-rose-600"> *</span> : null);
const KYC_FIELDS = { pan: ["pan_number", "pan_file_id"], kyc: ["kyc_doc_type", "kyc_doc_number", "kyc_file_id"], bank: ["bank_name", "account_holder", "account_number", "ifsc", "cheque_file_id"], declaration: ["declaration_file_id", "declaration_valid_till"] };
const RULE_FIELDS = { name: ["name"], contact_person: ["contact_person"], phone: ["phone"], address: ["address"], category: ["category"], ...KYC_FIELDS };

function KycBadge({ v }) {
  const ok = v.kyc_status === "verified";
  const pending = Object.entries({ pan: "PAN", kyc: "Gumasta/Aadhaar", bank: "Bank/Cheque", declaration: "FY Declaration" }).filter(([k]) => KYC_FIELDS[k].some((f) => !v[f])).map(([, l]) => l);
  return (
    <span data-testid={`vendor-kyc-badge-${v.id}`} title={pending.length ? `Pending: ${pending.join(", ")}` : "All KYC documents uploaded"}
      className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold ${ok ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300" : "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300"}`}>
      {ok ? "Fully Verified" : `Partial KYC · ${pending.join(", ")}`}
    </span>
  );
}

const EMPTY = { name: "", contact_person: "", phone: "", address: "", category: "", pan_number: "", pan_file_id: null, kyc_doc_type: "Gumasta License", kyc_doc_number: "", kyc_file_id: null, bank_name: "", account_holder: "", account_number: "", ifsc: "", cheque_file_id: null, declaration_file_id: null, declaration_fy: "", declaration_valid_till: "", default_tds_percent: 1, opening_balance: "", opening_type: "pay", opening_date: "" };

function Section({ title, children }) {
  return (
    <div className="rounded-xl border p-4">
      <div className="label-xs mb-3">{title}</div>
      <div className="grid gap-3 sm:grid-cols-2">{children}</div>
    </div>
  );
}

export function VendorDialog({ open, initial, onClose, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const { canFin, isAdmin } = useMarketRoles();
  const rcd = useDriverRules(EP);
  const { rules: R, labels } = rcd;
  useEffect(() => { if (open) setF(initial ? { ...EMPTY, ...initial } : EMPTY); }, [open, initial]);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const inp = (k, testid, props = {}) => <Input data-testid={testid} value={f[k] ?? ""} onChange={set(k)} {...props} />;
  const save = async (e) => {
    e.preventDefault();
    const miss = Object.keys(RULE_FIELDS).filter((k) => R[k] && RULE_FIELDS[k].some((x) => !String(f[x] ?? "").trim())).map((k) => labels[k] || k);
    if (miss.length) return toast.error(`Required: ${miss.join(", ")}`);
    setBusy(true);
    try {
      const body = { ...f, default_tds_percent: f.tds_exempt ? 0 : Number(f.default_tds_percent) || 0, tds_exempt: !!f.tds_exempt, ...openingBody(f, "pay") };
      const { data } = initial ? await api.put(`/market/vendors/${initial.id}`, body) : await api.post("/market/vendors", body);
      toast.success(initial ? "Vendor updated" : "Vendor registered");
      onSaved(data);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto bg-card" data-testid="vendor-dialog">
        <DialogHeader className="flex-row flex-wrap items-center justify-between gap-2 pr-8"><DialogTitle>{initial ? "Edit Vendor" : "Vendor Registration"}</DialogTitle>
          {isAdmin && <DriverRulesDialog cfg={rcd} onSaved={rcd.setCfg} endpoint={EP} title="Vendor Registration Fields" testid="vendor-form-field-settings" entity="Vendor" />}
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <Section title="Basic & Business Info">
            <Field label="GSTIN (details apne aap bharengi)"><GstinInput testid="vendor-gstin-input" value={f.gstin} onChange={(g) => setF((x) => ({ ...x, gstin: g }))}
              onFetched={(d) => setF((x) => fillFrom(x, d, { name: "name", address: "address", pan_number: "pan" }))} /></Field>
            <Field label={<>Vendor / Business Name{star(true)}</>}>{inp("name", "vendor-name-input")}</Field>
            <Field label={<>Contact Person Name{star(R.contact_person)}</>}>{inp("contact_person", "vendor-contact-input")}</Field>
            <Field label={<>Primary Mobile (WhatsApp){star(R.phone)}</>}>{inp("phone", "vendor-phone-input", { maxLength: 10, inputMode: "numeric" })}</Field>
            <Field label={<>Vendor Category{star(R.category)}</>}>
              <select data-testid="vendor-category-select" className={nativeInput} value={f.category} onChange={set("category")}>
                <option value="">— Select —</option>
                {VENDOR_CATS.map((c) => <option key={c}>{c}</option>)}
              </select>
            </Field>
            <Field label={<>Office Address{star(R.address)}</>} className="sm:col-span-2">{inp("address", "vendor-address-input")}</Field>
          </Section>
          <Section title={<>PAN Card{star(R.pan)}</>}>
            <Field label={<>PAN Number{star(R.pan)}</>}>{inp("pan_number", "vendor-pan-input", { maxLength: 10, className: "uppercase" })}</Field>
            <Field label={<>PAN Card Photo / PDF{star(R.pan)}</>}><DocUpload label="PAN" refType="vendor_doc" required={R.pan} value={f.pan_file_id} onChange={set("pan_file_id")} testid="vendor-pan-upload" /></Field>
          </Section>
          <Section title={<>Business Proof (Gumasta) or Aadhaar{star(R.kyc)}</>}>
            <Field label={<>Document Type{star(R.kyc)}</>}>
              <select data-testid="vendor-kyc-type-select" className={nativeInput} value={f.kyc_doc_type} onChange={set("kyc_doc_type")}>
                <option value="">— Select —</option>
                {KYC_TYPES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </Field>
            <Field label={<>Document Number{star(R.kyc)}</>}>{inp("kyc_doc_number", "vendor-kyc-number-input")}</Field>
            <Field label={<>Document File{star(R.kyc)}</>} className="sm:col-span-2"><DocUpload label={f.kyc_doc_type || "KYC document"} refType="vendor_doc" required={R.kyc} value={f.kyc_file_id} onChange={set("kyc_file_id")} testid="vendor-kyc-upload" /></Field>
            <p className="text-[11px] text-muted-foreground sm:col-span-2">KYC documents are stored privately and only visible to your team.</p>
          </Section>
          <Section title={<>Bank Account Proof{star(R.bank)}</>}>
            <Field label={<>Bank Name{star(R.bank)}</>}>{inp("bank_name", "vendor-bank-input")}</Field>
            <Field label={<>Account Holder Name{star(R.bank)}</>}>{inp("account_holder", "vendor-holder-input")}</Field>
            <Field label={<>Account Number{star(R.bank)}</>}>{inp("account_number", "vendor-account-input")}</Field>
            <Field label={<>IFSC Code{star(R.bank)}</>}>{inp("ifsc", "vendor-ifsc-input", { maxLength: 11, className: "uppercase" })}</Field>
            <Field label={<>Cancelled Cheque / Passbook{star(R.bank)}</>} className="sm:col-span-2"><DocUpload label="Cheque / Passbook" refType="vendor_doc" required={R.bank} value={f.cheque_file_id} onChange={set("cheque_file_id")} testid="vendor-cheque-upload" /></Field>
          </Section>
          <Section title={<>Financial Year Declaration (194C) & TDS{star(R.declaration)}</>}>
            <Field label={<>Declaration Letter{star(R.declaration)}</>}><DocUpload label="Declaration" refType="vendor_doc" required={R.declaration} value={f.declaration_file_id} onChange={set("declaration_file_id")} testid="vendor-declaration-upload" /></Field>
            <Field label="Financial Year">{inp("declaration_fy", "vendor-fy-input", { placeholder: "2026-27" })}</Field>
            <Field label={<>Valid Till (Expiry){star(R.declaration || !!f.declaration_file_id)}</>}>{inp("declaration_valid_till", "vendor-valid-till-input", { type: "date" })}</Field>
            <Field label="Default TDS % (when no valid declaration)">{inp("default_tds_percent", "vendor-tds-input", { type: "number", step: "0.01", min: 0, max: 20, disabled: !!f.tds_exempt })}</Field>
            <label className="flex items-center gap-2 text-sm" data-testid="vendor-tds-exempt-row"><input type="checkbox" data-testid="vendor-tds-exempt" checked={!!f.tds_exempt} onChange={(e) => setF({ ...f, tds_exempt: e.target.checked, default_tds_percent: e.target.checked ? 0 : f.default_tds_percent })} /><b>TDS Exempt (0%)</b><span className="text-xs text-muted-foreground">— is vendor ki kisi trip / payment me TDS nahi katega</span></label>
          </Section>
          {canFin && <OpeningFields f={f} setF={setF} kind="vendor" />}
          {initial?.id && <DocHistory entity="vendor" refId={initial.id} />}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" data-testid="vendor-save-btn" disabled={busy}>{initial ? "Save Changes" : "Register Vendor"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function MarketVendors() {
  const { canFin, canOps, isAdmin } = useMarketRoles();
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [dlg, setDlg] = useState({ open: false, initial: null });
  const rc = useDriverRules(EP);
  const load = () => api.get("/market/vendors").then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const rows = useMemo(() => list.filter((v) => `${v.name} ${v.phone} ${v.pan_number} ${v.contact_person}`.toLowerCase().includes(q.toLowerCase())), [list, q]);
  const del = async (v) => {
    if (!window.confirm(`Delete vendor ${v.name}?`)) return;
    try { await api.delete(`/market/vendors/${v.id}`); toast.success("Vendor deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageHeader title="Vendors" subtitle="Market vehicle owners, fleet transporters & commission agents — KYC register">
        <SearchBox value={q} onChange={setQ} placeholder="Search name, phone, PAN" testid="vendor-search" />
        {isAdmin && <DriverRulesDialog cfg={rc} onSaved={rc.setCfg} endpoint={EP} title="Vendor Registration Fields" testid="vendor-field-settings" entity="Vendor" />}
        {(canFin || canOps) && <Button data-testid="add-vendor-btn" onClick={() => setDlg({ open: true, initial: null })}><Plus className="mr-1 h-4 w-4" />Register Vendor</Button>}
      </PageHeader>
      <TableWrap>
        <thead><tr>{["Vendor", "Category", "Mobile", "PAN", "Declaration", "Trips", ...(canFin ? ["Net Payable", "Outstanding"] : []), ""].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((v) => (
            <tr key={v.id} className={tr} data-testid={`vendor-row-${v.id}`}>
              <td className={td}><div className="font-semibold">{v.name}</div><div className="text-xs text-muted-foreground">{v.contact_person}</div><KycBadge v={v} /></td>
              <td className={td}>{v.category}</td>
              <td className={td}>{v.phone}</td>
              <td className={`${td} font-mono`}>{v.pan_number}</td>
              <td className={td}>
                {v.declaration_valid
                  ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600"><ShieldCheck className="h-3.5 w-3.5" />Valid till {fmtDate(v.declaration_valid_till)}</span>
                  : <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600"><ShieldAlert className="h-3.5 w-3.5" />{v.tds_exempt ? "TDS Exempt · 0%" : `${v.declaration_file_id ? "Expired" : "None"} · TDS ${v.default_tds_percent}%`}</span>}
              </td>
              <td className={td}>{v.stats?.trips || 0}</td>
              {canFin && <td className={`${td} amount`}>{inr(v.stats?.net_payable)}</td>}
              {canFin && <td className={`${td} amount font-semibold ${v.stats?.outstanding > 0 ? "text-rose-600" : ""}`} data-testid={`vendor-outstanding-${v.id}`}>{inr(v.stats?.outstanding)}</td>}
              <td className={td}>
                <div className="flex justify-end gap-1">
                  {canFin && <Button asChild size="sm" variant="outline" data-testid={`statement-vendor-${v.id}`}><Link to={`/market/vendors/${v.id}/statement`}><FileText className="mr-1 h-3.5 w-3.5" />Statement</Link></Button>}
                  {canFin && <Button size="icon" variant="ghost" data-testid={`edit-vendor-${v.id}`} onClick={() => setDlg({ open: true, initial: v })}><Pencil className="h-4 w-4" /></Button>}
                  {isAdmin && <Button size="icon" variant="ghost" data-testid={`delete-vendor-${v.id}`} onClick={() => del(v)}><Trash2 className="h-4 w-4 text-rose-600" /></Button>}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </TableWrap>
      {!rows.length && <Empty testid="vendors-empty" text="No vendors registered yet" />}
      <VendorDialog key={JSON.stringify(rc.rules)} open={dlg.open} initial={dlg.initial} onClose={() => setDlg({ open: false, initial: null })} onSaved={() => { setDlg({ open: false, initial: null }); load(); }} />
    </div>
  );
}
